/**
 * Home Page - WorkStream Marketing
 * Landing page com novo branding
 */
import { ChevronRight, Check, Star, Mail, Phone, MapPin, ScanFace, Activity, Brain, HardHat, Play } from 'lucide-react'
import { useState } from 'react'
import SEO, { organizationSchema, softwareApplicationSchema, faqSchema } from '../components/SEO'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function Home() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    setEmail('')
    setTimeout(() => setSubmitted(false), 3000)
  }

  const faqs = [
    {
      question: 'O que é o WorkStream?',
      answer: 'WorkStream é uma plataforma SaaS de monitoramento inteligente que usa visão computacional e IA para oferecer ponto facial, análise de produtividade e verificação de EPIs em tempo real.',
    },
    {
      question: 'Quais são os principais serviços?',
      answer: 'Oferecemos 3 serviços principais: Ponto Facial com reconhecimento biométrico, Análise de Produtividade com IA comportamental, e Verificação de EPIs com compliance de NRs.',
    },
    {
      question: 'Como funciona o período de teste gratuito?',
      answer: 'Oferecemos 14 dias de teste gratuito sem necessidade de cartão de crédito. Você terá acesso completo a todas as funcionalidades para avaliar a plataforma.',
    },
    {
      question: 'A plataforma é compatível com LGPD?',
      answer: 'Sim, o WorkStream é 100% compatível com a LGPD. Seus dados são criptografados e você tem controle total sobre exportação e exclusão.',
    },
  ]

  const services = [
    {
      icon: ScanFace,
      title: 'Ponto Facial',
      description: 'Reconhecimento facial com 99.2% de precisão. Geolocalização, anti-spoofing e relatórios em tempo real.',
      color: 'cyan',
      badge: '🎯',
    },
    {
      icon: Activity,
      title: 'Análise de Produtividade',
      description: 'Monitore postura, ergonomia e atividades. Identifique fadiga e otimize a performance da equipe.',
      color: 'blue',
      badge: '📊',
    },
    {
      icon: HardHat,
      title: 'Verificação de EPIs',
      description: 'Detecção automática de 15+ tipos de EPIs. Conformidade com NR-6, NR-35 e alertas em tempo real.',
      color: 'orange',
      badge: '🦺',
    },
    {
      icon: Brain,
      title: 'IA Comportamental',
      description: 'Machine learning que aprende e prediz performance. Recomendações personalizadas para cada colaborador.',
      color: 'emerald',
      badge: '🧠',
    },
  ]

  return (
    <>
      <SEO
        title="WorkStream - Visão que move o trabalho"
        description="Plataforma SaaS de monitoramento inteligente com visão computacional e IA. Ponto facial, produtividade e verificação de EPIs. Teste grátis por 14 dias."
        keywords={[
          'ponto facial',
          'reconhecimento facial',
          'análise de produtividade',
          'verificação de EPIs',
          'visão computacional',
          'machine learning',
          'gestão de equipes',
          'compliance NR',
        ]}
        url="https://workstream.com.br"
        structuredData={[organizationSchema, softwareApplicationSchema, faqSchema(faqs)]}
      />

      <div className="min-h-screen bg-[#0B1120] text-white">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-20 pb-32 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-transparent rounded-full blur-3xl" />
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.03) 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-mono mb-8">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                NOVA PLATAFORMA 2025
              </div>

              {/* Main Heading */}
              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-white">Visão que move</span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  o trabalho
                </span>
              </h1>

              <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
                Monitoramento inteligente com visão computacional e IA. 
                Ponto facial, produtividade e segurança em uma única plataforma.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap justify-center gap-4 mb-16">
                <button 
                  className="px-8 py-4 rounded-xl font-semibold text-white flex items-center gap-2 transition-all hover:-translate-y-1"
                  style={{ 
                    background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                    boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
                  }}
                >
                  Começar Grátis
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button className="px-8 py-4 rounded-xl font-semibold bg-slate-800 text-white border border-slate-700 flex items-center gap-2 hover:bg-slate-700 transition-all">
                  <Play className="w-5 h-5" />
                  Ver Demo
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-8 max-w-3xl mx-auto">
                {[
                  { value: '127+', label: 'Empresas' },
                  { value: '2.8K+', label: 'Colaboradores' },
                  { value: '99.2%', label: 'Precisão' },
                  { value: '99.9%', label: 'Uptime' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-[#0F172A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">SERVIÇOS</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">
                Tudo que sua equipe precisa
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Quatro serviços poderosos integrados em uma única plataforma
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => {
                const Icon = service.icon
                const colorClasses: Record<string, { bg: string; border: string; text: string }> = {
                  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
                  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
                  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
                  orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400' },
                }
                const colors = colorClasses[service.color]

                return (
                  <div 
                    key={service.title}
                    className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-slate-600 transition-all group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <span className="text-2xl">{service.badge}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-slate-400 text-sm">{service.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Segments Section */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">SEGMENTOS</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">
                Para todos os setores
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { emoji: '🏗️', name: 'Construção Civil', desc: 'NRs, EPIs, produtividade' },
                { emoji: '📡', name: 'Telecomunicações', desc: 'Equipes de campo, ponto remoto' },
                { emoji: '🏥', name: 'Hospitais', desc: 'Turnos, compliance, segurança' },
                { emoji: '🏭', name: 'Indústria', desc: 'Linhas de produção, eficiência' },
              ].map((segment) => (
                <div 
                  key={segment.name}
                  className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/30 transition-all text-center group cursor-pointer"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">{segment.emoji}</div>
                  <h3 className="text-lg font-bold text-white mb-1">{segment.name}</h3>
                  <p className="text-sm text-slate-500">{segment.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24 bg-[#0F172A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">PLANOS</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">
                Preços simples e transparentes
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  name: 'Starter',
                  price: 'R$ 10',
                  unit: '/colaborador/mês',
                  description: 'Para pequenas equipes',
                  features: ['Ponto Facial básico', 'Até 50 colaboradores', 'Relatórios mensais', 'Suporte por email'],
                  color: 'slate',
                },
                {
                  name: 'Business',
                  price: 'R$ 25',
                  unit: '/colaborador/mês',
                  description: 'Mais popular',
                  features: ['Todos os serviços', 'Até 500 colaboradores', 'IA Comportamental', 'Relatórios em tempo real', 'API access', 'Suporte prioritário'],
                  highlighted: true,
                  color: 'cyan',
                },
                {
                  name: 'Enterprise',
                  price: 'Sob consulta',
                  unit: '',
                  description: 'Para grandes operações',
                  features: ['Colaboradores ilimitados', 'ML personalizado', 'On-premise disponível', 'SLA 99.99%', 'Suporte 24/7', 'Gerente dedicado'],
                  color: 'violet',
                },
              ].map((plan) => (
                <div
                  key={plan.name}
                  className={`p-8 rounded-2xl transition-transform ${
                    plan.highlighted
                      ? 'bg-gradient-to-b from-cyan-500/10 to-transparent border-2 border-cyan-500/30 transform scale-105'
                      : 'bg-slate-800/30 border border-slate-700/50'
                  }`}
                >
                  {plan.highlighted && (
                    <div className="text-center mb-4">
                      <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-semibold">
                        MAIS POPULAR
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-500 text-sm">{plan.unit}</span>
                  </div>
                  <button
                    className={`w-full py-3 rounded-xl font-semibold mb-6 transition-all ${
                      plan.highlighted
                        ? 'text-white'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                    style={plan.highlighted ? { 
                      background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                      boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
                    } : {}}
                  >
                    Começar Agora
                  </button>
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">DEPOIMENTOS</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-4">
                O que nossos clientes dizem
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Carlos Silva',
                  role: 'Gerente de Operações',
                  company: 'ContruBem S.A.',
                  text: 'O WorkStream revolucionou nossa gestão de ponto. Reduzimos fraudes em 98% e ganhamos horas de produtividade.',
                },
                {
                  name: 'Ana Santos',
                  role: 'Diretora de RH',
                  company: 'TeleNord Comunicações',
                  text: 'A análise de produtividade nos ajudou a identificar gargalos que não víamos. ROI em menos de 2 meses.',
                },
                {
                  name: 'Roberto Costa',
                  role: 'SESMT',
                  company: 'Hospital Santa Cruz',
                  text: 'Compliance com NRs nunca foi tão fácil. A detecção de EPIs em tempo real salvou vidas literalmente.',
                },
              ].map((testimonial) => (
                <div key={testimonial.name} className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-6">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                    <p className="text-xs text-cyan-400">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-[#0F172A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <span className="text-cyan-400 font-mono text-sm tracking-wider">CONTATO</span>
                <h2 className="text-4xl font-bold text-white mt-4 mb-6">
                  Fale Conosco
                </h2>
                <p className="text-slate-400 mb-8">
                  Nossa equipe está pronta para ajudar você a transformar sua gestão de equipes.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <Mail className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Email</p>
                      <p className="text-slate-400">contato@workstream.com.br</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <Phone className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Telefone</p>
                      <p className="text-slate-400">+55 (11) 3000-0000</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Localização</p>
                      <p className="text-slate-400">São Paulo, SP - Brasil</p>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                <h3 className="text-xl font-bold text-white mb-6">Envie uma mensagem</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Seu Nome"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="Seu Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none transition-colors"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Empresa"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none transition-colors"
                  />
                  <textarea
                    placeholder="Sua Mensagem"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none transition-colors resize-none"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-1"
                    style={{ 
                      background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                      boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
                    }}
                  >
                    Enviar Mensagem
                  </button>
                  {submitted && (
                    <p className="text-emerald-400 text-sm text-center">
                      ✅ Mensagem enviada com sucesso!
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/5 to-transparent" />
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Pronto para transformar sua gestão?
            </h2>
            <p className="text-xl text-slate-400 mb-8">
              Comece gratuitamente por 14 dias. Sem cartão de crédito.
            </p>
            <button 
              className="px-8 py-4 rounded-xl font-semibold text-white flex items-center gap-2 mx-auto transition-all hover:-translate-y-1"
              style={{ 
                background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
              }}
            >
              Começar Teste Grátis
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
